/**
 * Meta-Version Service for Cache Invalidation System
 * 
 * Handles communication with the database to track schema changes and
 * coordinate client-side cache invalidation when database resets occur.
 * 
 * Phase 1 implementation - provides foundation for cache invalidation handshake
 */

import { supabase } from '@/lib/supabase';

// Local storage key for storing the current meta-version
const META_VERSION_KEY = 'terrarium_meta_version';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

export interface MetaVersionResult {
  version: string;
  hasChanged: boolean;
  previousVersion: string | null;
}

/**
 * Fetches the current meta-version from the database
 * This represents the "ground truth" of the database state
 */
export async function fetchCurrentMetaVersion(): Promise<string> {
  try {
    const { data, error } = await supabase.rpc('get_meta_version');
    
    if (error) {
      console.error('MetaVersion: Failed to fetch current version:', error);
      // Fallback to timestamp-based version in case of error
      return Date.now().toString();
    }
    
    return data as string;
  } catch (err) {
    console.error('MetaVersion: Exception during version fetch:', err);
    return Date.now().toString();
  }
}

/**
 * Gets the locally stored meta-version
 * Returns null if no version is stored (first run or after cache clear)
 */
export function getStoredMetaVersion(): string | null {
  try {
    return localStorage.getItem(META_VERSION_KEY);
  } catch (err) {
    console.warn('MetaVersion: Failed to read stored version:', err);
    return null;
  }
}

/**
 * Stores the meta-version locally
 */
export function setStoredMetaVersion(version: string): void {
  try {
    localStorage.setItem(META_VERSION_KEY, version);
  } catch (err) {
    console.error('MetaVersion: Failed to store version:', err);
  }
}

/**
 * Performs the meta-version handshake to detect database changes
 * This is the core function called during app boot
 */
export async function performMetaVersionHandshake(): Promise<MetaVersionResult> {
  const storedVersion = getStoredMetaVersion();
  const currentVersion = await fetchCurrentMetaVersion();
  
  const hasChanged = storedVersion !== null && storedVersion !== currentVersion;
  
  // Log the handshake result for debugging
  if (isDevelopment) {
    console.log('MetaVersion Handshake:', {
      stored: storedVersion,
      current: currentVersion,
      hasChanged,
      isFirstRun: storedVersion === null
    });
  }
  
  // Update stored version to current
  setStoredMetaVersion(currentVersion);
  
  return {
    version: currentVersion,
    hasChanged,
    previousVersion: storedVersion
  };
}

/**
 * Clears the stored meta-version
 * Used during cache invalidation to force a fresh handshake
 */
export function clearStoredMetaVersion(): void {
  try {
    localStorage.removeItem(META_VERSION_KEY);
  } catch (err) {
    console.error('MetaVersion: Failed to clear stored version:', err);
  }
}

/**
 * Development utility to simulate a database reset
 * This manually increments the database meta-version
 */
export async function simulateDatabaseReset(): Promise<string> {
  if (!isDevelopment) {
    console.warn('MetaVersion: simulateDatabaseReset only available in development');
    return '';
  }
  
  try {
    const { data, error } = await supabase.rpc('increment_meta_version');
    
    if (error) {
      console.error('MetaVersion: Failed to simulate reset:', error);
      return '';
    }
    
    return data as string;
  } catch (err) {
    console.error('MetaVersion: Exception during reset simulation:', err);
    return '';
  }
}